import React, { useMemo, useState, useEffect } from 'react';
import { CRM_PIPELINES } from '../constants/pipelines';
import { computeLeadScoreFromRules } from '../utils/leadScoreRules';
import { estimateWinProbabilityPercent } from '../utils/winProbabilityHeuristic';
import { timeLogService } from '../services/timeLogService';
import { sequencesService } from '../services/sequencesService';
import { leadCommentsService } from '../services/leadCommentsService';
import { activityService } from '../services/activityService';
import HandoffLeadModal from './HandoffLeadModal';

function LeftMini({ title, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

export default function LeadCRMExtensions({
  editData,
  lead,
  leads = [],
  activities = [],
  currentUser,
  authUsers = [],
  onUpdateField,
  onHandoff,
  userScopeId,
  onCommentMentions,
}) {
  const uid = userScopeId ?? currentUser?.id ?? null;
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [timeMin, setTimeMin] = useState(30);
  const [timeNote, setTimeNote] = useState('');
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [timeTick, setTimeTick] = useState(0);

  const templates = sequencesService.getTemplates();
  const seqProg = sequencesService.getProgress(uid, lead?.id);

  useEffect(() => {
    setComments(leadCommentsService.getThread(uid, lead?.id));
  }, [uid, lead?.id, activities.length]);

  const parentOptions = useMemo(
    () => leads.filter(l => String(l.id) !== String(lead?.id)),
    [leads, lead?.id],
  );

  const childCount = useMemo(
    () => leads.filter(l => String(l.parentLeadId) === String(lead?.id)).length,
    [leads, lead?.id],
  );

  const rollupActs = useMemo(() => {
    const ids = leads.filter(l => String(l.parentLeadId) === String(lead?.id)).map(l => l.id);
    let n = 0;
    ids.forEach(id => { n += activityService.getForLead(id).length; });
    return n;
  }, [leads, lead?.id, activities.length]);

  const timeSum = useMemo(() => {
    void timeTick;
    return timeLogService.sumMinutesForLead(uid, lead?.id);
  }, [uid, lead?.id, timeTick]);

  const winPct = useMemo(() => estimateWinProbabilityPercent(editData, leads), [editData, leads]);
  const suggested = useMemo(() => computeLeadScoreFromRules(editData, activities), [editData, activities]);

  if (!lead) return null;

  return (
    <>
      <div style={{ padding: '0 18px 18px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>CRM+</div>

        <LeftMini title="Pipeline">
          <select
            value={editData.pipelineId || 'new_business'}
            onChange={e => onUpdateField('pipelineId', e.target.value)}
            style={{ width: '100%', height: 34, borderRadius: 7, border: '1px solid #dee4de', fontSize: 12, background: 'var(--bg2)' }}
          >
            {CRM_PIPELINES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </LeftMini>

        <LeftMini title="Übergeordnete Organisation (Zweigstelle)">
          <select
            value={editData.parentLeadId || ''}
            onChange={e => onUpdateField('parentLeadId', e.target.value || null)}
            style={{ width: '100%', height: 34, borderRadius: 7, border: '1px solid #dee4de', fontSize: 12, background: 'var(--bg2)' }}
          >
            <option value="">— Kein Haupt-Kontakt —</option>
            {parentOptions.map(p => (
              <option key={p.id} value={p.id}>{p.name} · {p.company}</option>
            ))}
          </select>
          {childCount > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{childCount} untergeordnete · {rollupActs} Aktivitäten (Standorte)</div>
          )}
        </LeftMini>

        <LeftMini title="Quelle">
          <input
            value={editData.source || ''}
            onChange={e => onUpdateField('source', e.target.value)}
            placeholder="z. B. Web, Messe, Empfehlung"
            style={{ width: '100%', height: 32, borderRadius: 7, border: '1px solid #dee4de', fontSize: 12, padding: '0 8px', boxSizing: 'border-box', background: 'var(--bg2)' }}
          />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Heuristik Gewinnchance: <strong>{winPct}%</strong></div>
        </LeftMini>

        <LeftMini title="Tags (Komma)">
          <input
            value={(editData.tags || []).join(', ')}
            onChange={e => onUpdateField('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            placeholder="SHK, Bayern, Key-Account"
            style={{ width: '100%', height: 32, borderRadius: 7, border: '1px solid #dee4de', fontSize: 12, padding: '0 8px', boxSizing: 'border-box', background: 'var(--bg2)' }}
          />
        </LeftMini>

        <LeftMini title="Score aus Regeln">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{suggested}</span>
            <button type="button" onClick={() => onUpdateField('score', suggested)}
              style={{ height: 28, padding: '0 10px', borderRadius: 6, border: '1px solid var(--accent)', background: 'var(--accent-bg)', color: 'var(--accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              Übernehmen
            </button>
          </div>
        </LeftMini>

        <LeftMini title="Angebotspositionen">
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Bezeichnung · Menge · Einzelpreis</div>
          {(editData.offerLineItems || []).map((row, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 48px 72px', gap: 4, marginBottom: 4 }}>
              <input value={row.label || ''} placeholder="Position"
                onChange={e => {
                  const next = [...(editData.offerLineItems || [])];
                  next[idx] = { ...next[idx], label: e.target.value };
                  onUpdateField('offerLineItems', next);
                }}
                style={{ height: 28, fontSize: 11, borderRadius: 6, border: '1px solid var(--border)', padding: '0 6px' }} />
              <input type="number" value={row.qty ?? 1}
                onChange={e => {
                  const next = [...(editData.offerLineItems || [])];
                  next[idx] = { ...next[idx], qty: Number(e.target.value) || 1 };
                  onUpdateField('offerLineItems', next);
                }}
                style={{ height: 28, fontSize: 11, borderRadius: 6, border: '1px solid var(--border)' }} />
              <input type="number" value={row.unitPrice ?? 0}
                onChange={e => {
                  const next = [...(editData.offerLineItems || [])];
                  next[idx] = { ...next[idx], unitPrice: Number(e.target.value) || 0 };
                  onUpdateField('offerLineItems', next);
                }}
                style={{ height: 28, fontSize: 11, borderRadius: 6, border: '1px solid var(--border)' }} />
            </div>
          ))}
          <button type="button" onClick={() => {
            const next = [...(editData.offerLineItems || []), { label: '', qty: 1, unitPrice: 0 }];
            onUpdateField('offerLineItems', next);
          }} style={{ fontSize: 11, marginTop: 4, cursor: 'pointer' }}>+ Position</button>
          <button type="button" onClick={() => {
            const sum = (editData.offerLineItems || []).reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unitPrice) || 0), 0);
            if (sum > 0) onUpdateField('offerAmount', Math.round(sum));
          }} style={{ marginLeft: 8, fontSize: 11, cursor: 'pointer' }}>Summe → Angebotsbetrag</button>
        </LeftMini>

        <LeftMini title="Zeiterfassung (Minuten)">
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Summe: {timeSum} min</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="number" value={timeMin} onChange={e => setTimeMin(Number(e.target.value) || 0)} style={{ width: 64, height: 28, borderRadius: 6, border: '1px solid var(--border)', fontSize: 12 }} />
            <input value={timeNote} onChange={e => setTimeNote(e.target.value)} placeholder="Notiz" style={{ flex: 1, height: 28, borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, padding: '0 6px' }} />
            <button type="button" onClick={() => {
              if (!timeMin) return;
              timeLogService.add(uid, { leadId: lead.id, minutes: timeMin, note: timeNote });
              setTimeNote('');
              setTimeMin(30);
              setTimeTick(t => t + 1);
            }} style={{ height: 28, padding: '0 8px', borderRadius: 6, background: 'var(--accent)', color: '#fff', border: 'none', fontSize: 11, cursor: 'pointer' }}>OK</button>
          </div>
        </LeftMini>

        <LeftMini title="Sequenz / Playbook">
          <select
            value={seqProg?.templateId || ''}
            onChange={e => {
              const v = e.target.value;
              if (v) sequencesService.start(uid, lead.id, v);
            }}
            style={{ width: '100%', height: 32, borderRadius: 6, border: '1px solid var(--border)', fontSize: 11, marginBottom: 6 }}
          >
            <option value="">— Vorlage wählen —</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {seqProg && (() => {
            const tpl = templates.find(t => t.id === seqProg.templateId);
            if (!tpl) return null;
            return (
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                {tpl.steps.map((s, i) => (
                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, cursor: 'pointer' }}>
                    <input type="checkbox" checked={(seqProg.doneStepIndexes || []).includes(i)} onChange={() => sequencesService.toggleStepDone(uid, lead.id, i)} />
                    Tag {s.day}: {s.label}
                  </label>
                ))}
              </div>
            );
          })()}
        </LeftMini>

        <LeftMini title="Kommentare (@Name)">
          <div style={{ maxHeight: 120, overflowY: 'auto', marginBottom: 6 }}>
            {comments.length === 0 && <div style={{ fontSize: 11, color: 'var(--text3)' }}>Noch keine Kommentare.</div>}
            {comments.map(c => (
              <div key={c.id} style={{ fontSize: 11, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <strong>{c.authorName}</strong> · {new Date(c.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                <div style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.text}</div>
              </div>
            ))}
          </div>
          <textarea value={commentText} onChange={e => setCommentText(e.target.value)} rows={2} placeholder="@Max Mustermann bitte prüfen …"
            style={{ width: '100%', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, padding: 6, resize: 'vertical', boxSizing: 'border-box' }} />
          <button type="button" onClick={() => {
            if (!commentText.trim()) return;
            const entry = leadCommentsService.add(uid, lead.id, {
              text: commentText,
              authorId: currentUser?.id,
              authorName: currentUser?.name || currentUser?.email || 'Nutzer',
              authUsers,
            });
            const preview = commentText.trim();
            setCommentText('');
            setComments(leadCommentsService.getThread(uid, lead.id));
            if (entry?.mentions?.length && onCommentMentions) {
              const targets = entry.mentions.filter(id => id && id !== currentUser?.id);
              if (targets.length) {
                onCommentMentions({
                  leadId: lead.id,
                  leadName: editData?.name || lead.name,
                  mentions: targets,
                  excerpt: preview,
                  authorName: currentUser?.name || currentUser?.email || 'Nutzer',
                });
              }
            }
          }} style={{ marginTop: 6, height: 28, padding: '0 12px', borderRadius: 6, background: 'var(--bg3)', border: '1px solid var(--border)', fontSize: 11, cursor: 'pointer' }}>Senden</button>
        </LeftMini>

        {onHandoff && authUsers.length > 1 && (
          <button type="button" onClick={() => setHandoffOpen(true)}
            style={{ width: '100%', height: 34, borderRadius: 8, border: '1px solid #159c5f', background: 'rgba(29,190,116,0.08)', color: '#0f8a53', fontWeight: 600, fontSize: 12, cursor: 'pointer', marginTop: 4 }}>
            Lead übergeben…
          </button>
        )}
      </div>

      {handoffOpen && (
        <HandoffLeadModal
          lead={editData}
          authUsers={authUsers}
          currentUser={currentUser}
          onClose={() => setHandoffOpen(false)}
          onSubmit={({ targetUserId, message, includeSummary }) => {
            onHandoff?.({ leadId: lead.id, targetUserId, message, includeSummary });
            setHandoffOpen(false);
          }}
        />
      )}
    </>
  );
}
